import type { Client, Contact, Task } from '../types/crm';
import { clientById, contactLabel, contactsForClient } from '../utils/crmHelpers';
import { formatSingaporeDate, CRM_TIMEZONE, parseCrmDate, singaporeTodayDateString } from '../utils/dateTime';

export type TaskVisitListRow = {
  no: number;
  name: string;
  address: string;
  contactPerson: string;
  remark: string;
  sortKey: string;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** YYYY-MM-DD in Singapore for a task due (fallback created). */
export function taskVisitDateKey(task: Task): string | null {
  const d = parseCrmDate(task.due_date) ?? parseCrmDate(task.created_at);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Header date style: 05/08/2026 */
export function formatVisitListDate(isoOrKey: string): string {
  return formatSingaporeDate(isoOrKey);
}

function weekdayLong(isoOrKey: string): string {
  const d = parseCrmDate(isoOrKey);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: CRM_TIMEZONE,
    weekday: 'long',
  }).format(d);
}

/** e.g. Customer Visit List - Wednesday 5/8/2026 */
export function visitListHeader(fromDate: string, toDate: string): string {
  const from = fromDate.trim();
  const to = toDate.trim();
  if (!from && !to) return 'Customer Visit List';
  if (!to || from === to) {
    const key = from || to;
    const wd = weekdayLong(key);
    const date = formatVisitListDate(key);
    return wd && date
      ? `Customer Visit List - ${wd} ${date}`
      : `Customer Visit List - ${date || key}`;
  }
  return `Customer Visit List - ${formatVisitListDate(from)} to ${formatVisitListDate(to)}`;
}

function formatContactPerson(contacts: Contact[], clientId?: string | null): string {
  const list = contactsForClient(contacts, clientId);
  const primary = list[0];
  if (!primary) return '';
  const name = contactLabel(primary);
  const phone = primary.phone?.trim();
  if (name && phone) return `${name} (${phone})`;
  return name || phone || '';
}

/** Remark = Description / Notes; preserve line breaks. */
function formatRemark(description?: string | null): string {
  return (description ?? '').trim();
}

export function buildTaskVisitListRows(
  tasks: Task[],
  clients: Client[],
  contacts: Contact[],
  fromDate: string,
  toDate: string,
): TaskVisitListRow[] {
  const from = fromDate.trim();
  const to = toDate.trim() || from;
  if (!from) return [];

  const list = tasks
    .filter((task) => {
      const key = taskVisitDateKey(task);
      if (!key) return false;
      return key >= from && key <= to;
    })
    .sort((a, b) => {
      const da = taskVisitDateKey(a) ?? '';
      const db = taskVisitDateKey(b) ?? '';
      if (da !== db) return da.localeCompare(db);
      const ca = clientById(clients, a.client_id)?.company_name ?? '';
      const cb = clientById(clients, b.client_id)?.company_name ?? '';
      return ca.localeCompare(cb, undefined, { sensitivity: 'base' });
    });

  return list.map((task, index) => {
    const client = clientById(clients, task.client_id);
    return {
      no: index + 1,
      name: (client?.company_name ?? '').trim(),
      address: (client?.address ?? '').trim(),
      contactPerson: formatContactPerson(contacts, task.client_id),
      remark: formatRemark(task.description),
      sortKey: taskVisitDateKey(task) ?? '',
    };
  });
}

function textToHtml(text: string): string {
  if (!text) return '';
  return escapeHtml(text).replace(/\r\n|\r|\n/g, '<br/>');
}

export function renderTaskVisitListHtml(rows: TaskVisitListRow[], header: string): string {
  const bodyRows =
    rows.length === 0
      ? `<tr><td colspan="5" class="empty">No tasks in this period.</td></tr>`
      : rows
          .map(
            (r) => `<tr>
  <td class="col-no">${r.no}</td>
  <td class="col-name">${textToHtml(r.name)}</td>
  <td class="col-address">${textToHtml(r.address)}</td>
  <td class="col-contact">${textToHtml(r.contactPerson)}</td>
  <td class="col-remark">${textToHtml(r.remark)}</td>
</tr>`,
          )
          .join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(header)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 14px 16px 20px;
      font-family: Arial, Helvetica, sans-serif;
      color: #000;
      background: #fff;
    }
    /* Matches Daily sheet: Arial 14pt bold, centered across full width */
    h1 {
      margin: 0 0 10px;
      padding: 6px 8px;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14pt;
      font-weight: 700;
      text-align: center;
      line-height: 1.25;
      border: 1px solid #000;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11pt;
    }
    /* Match Daily sheet: Remark wider; Name / Address / Contact narrower */
    col.col-no { width: 4%; }
    col.col-name { width: 16%; }
    col.col-address { width: 16%; }
    col.col-contact { width: 14%; }
    col.col-remark { width: 50%; }
    thead th {
      border: 1px solid #000;
      padding: 6px 5px;
      font-size: 12pt;
      font-weight: 700;
      text-align: center;
      vertical-align: middle;
      height: 31px;
      background: #fff;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: anywhere;
    }
    tbody td {
      border: 1px solid #000;
      padding: 6px 5px;
      min-height: 78px;
      height: 78px;
      vertical-align: middle;
      text-align: center;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    tbody td.col-remark {
      text-align: left;
      vertical-align: top;
    }
    .empty {
      min-height: 78px;
      height: 78px;
      text-align: center;
      color: #444;
      vertical-align: middle;
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(header)}</h1>
  <table>
    <colgroup>
      <col class="col-no" />
      <col class="col-name" />
      <col class="col-address" />
      <col class="col-contact" />
      <col class="col-remark" />
    </colgroup>
    <thead>
      <tr>
        <th>No</th>
        <th>Name</th>
        <th>Address</th>
        <th>Contact Person</th>
        <th>Remark</th>
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
  </table>
</body>
</html>`;
}

function safeFilename(title: string): string {
  return title.replace(/[^\w.-]+/g, '_').replace(/_+/g, '_').slice(0, 90) || 'Customer_Visit_List';
}

export type TaskVisitListExportOptions = {
  fromDate: string;
  toDate: string;
};

/** Renders Customer Visit List (Daily sheet style) and downloads as PDF. */
export async function downloadTaskVisitListPdf(
  tasks: Task[],
  clients: Client[],
  contacts: Contact[],
  options: TaskVisitListExportOptions,
): Promise<void> {
  const fromDate = options.fromDate.trim() || singaporeTodayDateString();
  const toDate = options.toDate.trim() || fromDate;
  const rangeFrom = fromDate <= toDate ? fromDate : toDate;
  const rangeTo = fromDate <= toDate ? toDate : fromDate;

  const rows = buildTaskVisitListRows(tasks, clients, contacts, rangeFrom, rangeTo);
  const header = visitListHeader(rangeFrom, rangeTo);
  const html = renderTaskVisitListHtml(rows, header);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  // Landscape A4 ~ width at 96dpi
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:1123px;min-height:794px;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!doc) throw new Error('Could not prepare report document.');

    doc.open();
    doc.write(html);
    doc.close();

    await new Promise<void>((resolve) => {
      if (doc.readyState === 'complete') {
        resolve();
        return;
      }
      iframe.onload = () => resolve();
    });
    await new Promise((r) => setTimeout(r, 200));

    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);

    const canvas = await html2canvas(doc.body, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const usableWidth = pageWidth - margin * 2;
    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let offsetY = margin;

    pdf.addImage(imgData, 'PNG', margin, offsetY, imgWidth, imgHeight);
    heightLeft -= pageHeight - margin;

    while (heightLeft > 0) {
      offsetY = margin - (imgHeight - heightLeft);
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, offsetY, imgWidth, imgHeight);
      heightLeft -= pageHeight - margin;
    }

    pdf.save(`${safeFilename(header)}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}
